import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Tag, MapPin, ShoppingBag, AlertCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { validateCoupon } from '../services/couponService';
import {
  createOrder,
  markOrderFailed,
  computeOrderTotals,
  FREE_SHIPPING_THRESHOLD,
} from '../services/orderService';
import { createRazorpayOrder, verifyRazorpayPayment } from '../services/paymentService';
import { openRazorpayCheckout, isRazorpayConfigured } from '../utils/razorpay';
import { fetchAddresses, addAddress } from '../services/addressService';
import { INDIAN_STATES } from '../utils/indianStates';

export default function Checkout() {
  const navigate = useNavigate();
  const { items, total: subtotal, setIsOpen, clearCart } = useCart();
  const { user, profile, authLoading, setLoginModalOpen, setLoginRedirect } = useAuth();

  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  const [shipping, setShipping] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
  });
  const [errors, setErrors] = useState({});
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');

  const totals = useMemo(
    () => computeOrderTotals(subtotal, discountAmount),
    [subtotal, discountAmount]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsOpen(false);
  }, [setIsOpen]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoginRedirect('/checkout');
      setLoginModalOpen(true);
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate, setLoginModalOpen, setLoginRedirect]);

  // Load default saved address on mount
  useEffect(() => {
    if (!user) return;
    fetchAddresses(user.uid).then((addresses) => {
      const defaultAddr = addresses.find((a) => a.is_default) || addresses[0];
      if (defaultAddr) {
        setShipping({
          name: defaultAddr.name || '',
          phone: (defaultAddr.phone || '').replace(/^\+91/, ''),
          address: defaultAddr.address || '',
          city: defaultAddr.city || '',
          state: defaultAddr.state || '',
          pincode: defaultAddr.pincode || '',
        });
      }
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!authLoading && user && items.length === 0) {
      navigate('/shop', { replace: true });
    }
  }, [authLoading, user, items.length, navigate]);

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShipping((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateShipping = () => {
    const e = {};
    if (!shipping.name.trim()) e.name = 'Name is required';
    if (!/^\d{10}$/.test(shipping.phone.replace(/\D/g, ''))) e.phone = 'Enter a valid 10-digit number';
    if (!shipping.address.trim()) e.address = 'Address is required';
    if (!shipping.city.trim()) e.city = 'City is required';
    if (!shipping.state.trim()) e.state = 'Please select a state';
    if (!/^\d{6}$/.test(shipping.pincode)) e.pincode = 'Enter a valid 6-digit pincode';
    return e;
  };

  const handleApplyCoupon = async () => {
    setCouponError('');
    setCouponLoading(true);
    try {
      const result = await validateCoupon(couponInput, subtotal);
      if (!result.valid) {
        setCouponError(result.error);
        setAppliedCoupon(null);
        setDiscountAmount(0);
        return;
      }
      setAppliedCoupon(result.coupon);
      setDiscountAmount(result.discountAmount);
      setCouponInput(result.coupon.code);
    } catch (err) {
      console.error(err);
      setCouponError('Could not validate coupon. Please try again.');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setCouponInput('');
    setCouponError('');
  };

  const handlePay = async () => {
    setPayError('');
    const validationErrors = validateShipping();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    if (!isRazorpayConfigured()) {
      setPayError('Payment gateway is not configured. Please contact support.');
      return;
    }

    setPaying(true);
    let order = null;

    try {
      const phoneE164 = `+91${shipping.phone.replace(/\D/g, '').slice(-10)}`;

      order = await createOrder({
        firebaseUid: user.uid,
        profileId: profile?.id,
        items,
        subtotal: totals.subtotal,
        discountAmount: totals.discountAmount,
        shippingAmount: totals.shipping,
        totalAmount: totals.total,
        couponCode: appliedCoupon?.code,
        couponId: appliedCoupon?.id,
        shipping: {
          name: shipping.name.trim(),
          phone: phoneE164,
          email: profile?.email || '',
          address: shipping.address.trim(),
          city: shipping.city.trim(),
          state: shipping.state.trim(),
          pincode: shipping.pincode,
        },
      });

      const razorpayOrder = await createRazorpayOrder(order.id, totals.total);

      const paymentResponse = await openRazorpayCheckout({
        amount: totals.total,
        orderNumber: order.order_number,
        razorpayOrderId: razorpayOrder.razorpay_order_id,
        razorpayKeyId: razorpayOrder.key_id,
        customer: {
          name: shipping.name.trim(),
          email: profile?.email || '',
          phone: phoneE164,
        },
        onSuccess: async (response) => {
          await verifyRazorpayPayment({
            orderId: order.id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          // Save address to user's saved addresses if not already saved
          try {
            const existingAddresses = await fetchAddresses(user.uid);
            const isDuplicate = existingAddresses.some(
              (a) =>
                a.address === shipping.address.trim() &&
                a.city === shipping.city.trim() &&
                a.pincode === shipping.pincode
            );
            if (!isDuplicate) {
              await addAddress(user.uid, {
                label: 'Home',
                name: shipping.name.trim(),
                phone: phoneE164,
                address: shipping.address.trim(),
                city: shipping.city.trim(),
                state: shipping.state.trim(),
                pincode: shipping.pincode,
                is_default: existingAddresses.length === 0,
              });
            }
          } catch (_) {
            // Silently fail - address save is non-critical
          }
          clearCart();
          navigate(`/order-success/${order.id}`, { replace: true });
        },
        onDismiss: async () => {
          try {
            await markOrderFailed(order.id, 'Payment cancelled by user');
          } catch (_) {
            // Silently ignore - Edge Function may not be deployed yet
          }
        },
      });

      if (!paymentResponse) {
        setPayError('Payment was cancelled.');
      }
    } catch (err) {
      console.error(err);
      if (order) {
        try {
          await markOrderFailed(order.id, err.message || 'Payment failed');
        } catch (_) {
          // Silently ignore - Edge Function may not be deployed yet
        }
      }
      setPayError(err.message || 'Payment failed. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  if (authLoading || !user) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </main>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <main className="min-h-screen bg-white">
      <section className="bg-[#fdf8f6] py-12 sm:py-16 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white border border-gray-100 mb-4">
          <ShoppingBag size={20} className="text-charcoal" />
        </div>
        <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2">Secure Checkout</p>
        <h1 className="font-display text-4xl sm:text-5xl font-light">Checkout</h1>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left: shipping + coupon */}
          <div className="lg:col-span-3 space-y-10">
            <section>
              <div className="flex items-center gap-2 mb-6">
                <MapPin size={16} className="text-gray-400" />
                <h2 className="text-xs tracking-[0.2em] uppercase font-medium">Delivery Address</h2>
              </div>
              <div className="space-y-6">
                <Field
                  label="Full name"
                  name="name"
                  value={shipping.name}
                  onChange={handleShippingChange}
                  error={errors.name}
                />
                <Field
                  label="Phone"
                  name="phone"
                  inputMode="numeric"
                  value={shipping.phone}
                  onChange={(e) =>
                    handleShippingChange({
                      target: { name: 'phone', value: e.target.value.replace(/\D/g, '').slice(0, 10) },
                    })
                  }
                  error={errors.phone}
                />
                <Field
                  label="Address"
                  name="address"
                  as="textarea"
                  value={shipping.address}
                  onChange={handleShippingChange}
                  error={errors.address}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field
                    label="City"
                    name="city"
                    value={shipping.city}
                    onChange={handleShippingChange}
                    error={errors.city}
                  />
                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase text-gray-400 mb-1.5">State</label>
                    <select
                      name="state"
                      value={shipping.state}
                      onChange={handleShippingChange}
                      className={`w-full border-b py-2.5 text-sm bg-transparent outline-none font-light transition-colors ${
                        errors.state ? 'border-red-400' : 'border-gray-200 focus:border-black'
                      } ${!shipping.state ? 'text-gray-400' : 'text-black'}`}
                    >
                      <option value="" disabled>Select state</option>
                      {INDIAN_STATES.map((s) => (
                        <option key={s} value={s} className="text-black">{s}</option>
                      ))}
                    </select>
                    {errors.state && <p className="text-xs text-red-400 mt-1">{errors.state}</p>}
                  </div>
                  <Field
                    label="Pincode"
                    name="pincode"
                    inputMode="numeric"
                    value={shipping.pincode}
                    onChange={(e) =>
                      handleShippingChange({
                        target: { name: 'pincode', value: e.target.value.replace(/\D/g, '').slice(0, 6) },
                      })
                    }
                    error={errors.pincode}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Update your saved address anytime from{' '}
                <Link to="/profile" className="underline hover:text-black">Profile</Link>.
              </p>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-4">
                <Tag size={16} className="text-gray-400" />
                <h2 className="text-xs tracking-[0.2em] uppercase font-medium">Coupon Code</h2>
              </div>
              {appliedCoupon ? (
                <div className="flex items-center justify-between border border-green-200 bg-green-50 px-4 py-3 rounded">
                  <div>
                    <p className="text-sm font-medium text-green-800">{appliedCoupon.code} applied</p>
                    <p className="text-xs text-green-600">
                      You save ₹{discountAmount.toLocaleString()}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveCoupon}
                    className="text-xs tracking-widest uppercase text-green-700 hover:text-green-900"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="flex-1 border-b border-gray-200 focus:border-black outline-none py-2.5 text-sm bg-transparent uppercase tracking-wider"
                  />
                  <button
                    type="button"
                    onClick={handleApplyCoupon}
                    disabled={couponLoading || !couponInput.trim()}
                    className="px-5 py-2.5 text-xs tracking-[0.15em] uppercase border border-black hover:bg-black hover:text-white transition-colors disabled:opacity-40"
                  >
                    {couponLoading ? <Loader2 size={14} className="animate-spin" /> : 'Apply'}
                  </button>
                </div>
              )}
              {couponError && <p className="text-xs text-red-500 mt-2">{couponError}</p>}
            </section>
          </div>

          {/* Right: order summary */}
          <div className="lg:col-span-2">
            <div className="border border-gray-100 p-6 sticky top-24">
              <h2 className="text-xs tracking-[0.2em] uppercase font-medium mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-3">
                    <img src={item.image} alt={item.name} className="w-14 h-14 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{item.name}</p>
                      <p className="text-xs text-gray-400">Qty {item.qty} · {item.size}</p>
                      <p className="text-sm mt-0.5">₹{(item.price * item.qty).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 text-sm border-t border-gray-100 pt-4">
                <Row label="Subtotal" value={`₹${totals.subtotal.toLocaleString()}`} />
                {discountAmount > 0 && (
                  <Row label="Discount" value={`-₹${discountAmount.toLocaleString()}`} accent />
                )}
                <Row
                  label="Shipping"
                  value={totals.shipping === 0 ? 'Free' : `₹${totals.shipping.toLocaleString()}`}
                />
                {totals.shipping > 0 && (
                  <p className="text-xs text-[#cc7c66]">
                    Add ₹{(FREE_SHIPPING_THRESHOLD - subtotal).toLocaleString()} more for free shipping
                  </p>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                  <span className="font-medium">Total</span>
                  <span className="text-lg font-medium">₹{totals.total.toLocaleString()}</span>
                </div>
              </div>

              {payError && (
                <div className="flex items-start gap-2 mt-4 text-xs text-red-500">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{payError}</span>
                </div>
              )}

              <button
                type="button"
                onClick={handlePay}
                disabled={paying}
                className="w-full mt-6 bg-black text-white py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {paying ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Processing…
                  </>
                ) : (
                  `Pay ₹${totals.total.toLocaleString()}`
                )}
              </button>

              <p className="text-[10px] text-gray-400 text-center mt-4 leading-relaxed">
                Secured by Razorpay · UPI, Cards, Netbanking &amp; Wallets
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function Field({ label, error, as, ...props }) {
  const isTextarea = as === 'textarea';
  const inputProps = { ...props };
  delete inputProps.as;

  return (
    <div>
      <label className="block text-xs tracking-[0.15em] uppercase text-gray-400 mb-1.5">
        {label}
      </label>
      {isTextarea ? (
        <textarea
          {...inputProps}
          rows={3}
          className={`w-full border-b ${error ? 'border-red-400' : 'border-gray-200'} focus:border-black outline-none py-2.5 text-sm resize-none bg-transparent transition-colors font-light`}
        />
      ) : (
        <input
          {...inputProps}
          className={`w-full border-b ${error ? 'border-red-400' : 'border-gray-200'} focus:border-black outline-none py-2.5 text-sm bg-transparent transition-colors font-light`}
        />
      )}
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className={accent ? 'text-green-600' : ''}>{value}</span>
    </div>
  );
}
